import { ApolloClient, concat, createHttpLink, InMemoryCache, split } from "@apollo/client/core"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import type { Ref } from "vue"
import { GraphQLWsLink } from "@apollo/client/link/subscriptions"
import { createClient } from "graphql-ws"
import { getMainDefinition } from "@apollo/client/utilities"
import { captureException } from "@sentry/vue"
import { isExpectedGraphQlError, isExpectedNetworkError } from "./sentry-noise-filter"
import { notifyWsReconnected } from "./wsConnection"
import { serverBaseUrl } from "../config"

// Bounded requests/responses; a hung or half-open connection must eventually reject
// so the error reaches the user-facing notification path instead of hanging forever.
const HTTP_TIMEOUT_MS = 30_000

export function createApolloClient(token: Ref<string | null>, refreshToken: () => Promise<void>) {
  const isProduction = import.meta.env.PROD

  // Refresh a soon-to-expire token before every request instead of trusting the expiry
  // timer alone (see refreshTokenIfNeeded in auth/keycloak.ts).
  const authHeaderLink = setContext(async (_, { headers }) => {
    await refreshToken()
    return {
      headers: {
        ...headers,
        authorization: token.value ? `Bearer ${token.value}` : ""
      }
    }
  })

  // Abort a stalled HTTP GraphQL request after a bounded time so the promise rejects
  // (reaching watchErrors) rather than hanging forever (resilience-02).
  const timeoutFetch: typeof fetch = (input, init) => {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), HTTP_TIMEOUT_MS)
    return fetch(input, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer))
  }

  // HTTP connection to the API
  const httpLink = createHttpLink({
    uri: `${serverBaseUrl}/admin/graphql`,
    fetch: timeoutFetch,
  })

  // Report GraphQL and network errors to Sentry/GlitchTip, tagged with the operation
  // name, then let the request continue so the existing notifyError toast still fires
  // (observability-02). Expected errors (server-side ValidationError, Session-401,
  // Deploy-Transienten) are toast-only — see sentry-noise-filter.ts.
  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const operationName = operation.operationName
    if (graphQLErrors) {
      for (const graphQLError of graphQLErrors) {
        if (isExpectedGraphQlError(graphQLError)) continue
        captureException(new Error(graphQLError.message), {
          tags: { operation: operationName, kind: "graphql" },
          extra: { path: graphQLError.path, locations: graphQLError.locations },
        })
      }
    }
    if (networkError && !isExpectedNetworkError(networkError)) {
      captureException(networkError, { tags: { operation: operationName, kind: "network" } })
    }
  })

  const wsBaseUrl = serverBaseUrl
    .replace(/^http:/, "ws:")
    .replace(/^https:/, "wss:")

  const websocketLink = new GraphQLWsLink(
    createClient({
      url: `${wsBaseUrl}/admin/subscriptions`,
      // Send the bearer token in the graphql-ws connection_init handshake payload, not the
      // URL query string — the server reads it via BearerTokenAuthenticationExtractor. Keeps
      // the admin token out of access logs/history/telemetry (security-02). Re-evaluated on
      // each (re)connect so a refreshed token is used.
      connectionParams: async () => {
        await refreshToken()
        return { Authorization: token.value ? `Bearer ${token.value}` : "" }
      },
      on: {
        connected: (_socket, _payload, wasRetry) => {
          if (wasRetry) notifyWsReconnected()
        },
      },
    })
  )

  const requestLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      )
    },
    websocketLink,
    httpLink
  )

  const link = concat(authHeaderLink, concat(errorLink, requestLink))

  // Cache implementation
  const cache = new InMemoryCache()

  // Create the apollo client
  return new ApolloClient({
    // link: concat(authHeaderLink, concat(httpLink, websocketLink)),
    link: link,
    cache,
    defaultOptions: {
      query: {
        fetchPolicy: "no-cache",
        errorPolicy: "all"
      },
      watchQuery: {
        fetchPolicy: "no-cache",
        errorPolicy: "all"
      },
      mutate: {
        fetchPolicy: "no-cache",
        errorPolicy: "all"
      }
    },
    devtools: {
      enabled: !isProduction
    }
  })
}
